import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Form,
  Col,
  Row,
  Icon,
  Select,
  Steps,
  Switch,
  notification,
  message,
  Modal,
  Alert,
} from "antd";
import { withRouter } from "react-router-dom";
import api from "../../../services/api";
import "./index.css";
import Show from "../../../contexts/PermissionContext/Show";
import withPermission from "../../../contexts/PermissionContext/withPermission";
import { useFreire } from "../../../utils/freireContext";
import * as texts from "../locales";
import { sorter } from "../../../utils/sorter";
import SelectCountry from "../../../components/SelectCountry";
import { COUNTRY_BR } from "../../../constants/countries";
import { validate as isValidEmail } from "email-validator";
import {
  useWhitelabels,
  BRAND_WHITELABEL_ALIAS,
} from "../../../components/WhitelabelThemesProvider";
import { BRAND } from "../../../config";

import stationReportImg from "../../../assets/img/stationReport.png";
import MonthlyCouponReport from "../../../assets/img/monthlyCouponReport.png";
import quotaReportImg from "../../../assets/img/quota-consumption-alert.png";
import SelectWithNewItem from "../../../components/SelectWithNewItem";
import AllowedAppsForm from "../AllowedAppsForm";
import { getCompanyCurrentUser } from "../../../services/auth";
import { getCompanyCustomThemes } from "../../../services/companyCustomTheme";
import AlertParentCompanyIconPack from "../AlertParentCompanyIconPack";
import { DOCUMENT_TYPES } from "../../../constants/document-types";
import {
  formatDocumentByType,
  validateDocumentByType,
} from "../../../utils/document-masks";
import { FISCAL_MODEL_OPTIONS, SOFTWARE_LICENSE } from "../../../constants/fiscal-models";

const { Step } = Steps;
const { Option } = Select;

const BATCH_DURATION_OPTIONS = [
  { value: "daily", label: texts.BATCH_DURATION_TYPE_DAILY },
  { value: "weekly", label: texts.BATCH_DURATION_TYPE_WEEKLY },
  { value: "biweekly", label: texts.BATCH_DURATION_TYPE_BIWEEKLY },
  { value: "monthly", label: texts.BATCH_DURATION_TYPE_MONTHLY },
];

const NewCompany = ({ form, history, hasPermission }) => {
  const { freire } = useFreire();
  const { availableIconPacks } = useWhitelabels();

  const [userCompany, setUserCompany] = useState(null);
  const [hasIconPack, setHasIconPack] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [emails, setEmails] = useState([]);
  const [customerTypes, setCustomerType] = useState([]);
  const [loadingFetchData, setLoadingFetchData] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [customThemes, setCustomThemes] = useState([]);

  const [isModalVisible1, setIsModalVisible1] = useState(false);
  const [isModalVisible2, setIsModalVisible2] = useState(false);
  const [isModalVisible3, setIsModalVisible3] = useState(false);

  const [loading] = useState(false);
  const { getFieldDecorator } = form;

  const isTupiUser =
    userCompany?.id === BRAND && userCompany?.parentCompanyID === BRAND;

  const selectedDocumentType = form.getFieldValue("document_type");
  const isDocumentOptional = selectedDocumentType === DOCUMENT_TYPES.CUIT;

  // Track fiscal model selection to show contextual alerts
  const [selectedFiscalModel, setSelectedFiscalModel] = useState(
    form.getFieldValue("fiscal_model")
  );

  const handleDocumentTypeChange = useCallback(
    (value) => {
      const currentDocument = form.getFieldValue("document") ?? "";

      form.setFieldsValue({
        document_type: value,
        document: formatDocumentByType(currentDocument, value),
      });

      form.validateFields(["document"], { force: true }, () => {});
    },
    [form]
  );

  const validateCompanyDocument = useCallback(
    (_rule, value, callback) => {
      const documentType = form.getFieldValue("document_type");
      const trimmedValue = value?.trim();

      if (documentType === DOCUMENT_TYPES.CUIT) {
        if (!trimmedValue) {
          callback();
          return;
        }
      } else if (!trimmedValue) {
        callback(freire(texts.REQUIRED));
        return;
      }

      if (trimmedValue && !validateDocumentByType(trimmedValue, documentType)) {
        callback(freire(texts.INVALID_DOCUMENT_FORMAT));
        return;
      }

      callback();
    },
    [form, freire]
  );

  const getCustomerTypes = useCallback(async () => {
    try {
      const {
        data: { customerTypes },
      } = await api.get("customer-types");
      return customerTypes;
    } catch (error) {
      throw new Error(freire(texts.ERROR_CUSTOMER_TYPE));
    }
  }, [freire]);

  const getCustomThemes = useCallback(async () => {
    try {
      const themes = await getCompanyCustomThemes();
      return themes;
    } catch (error) {
      throw new Error(freire(texts.ERROR_LIST_CUSTOM_THEMES));
    }
  }, [freire]);

  const getCompanies = useCallback(async () => {
    try {
      const { data: companies } = await api.get("company");

      const companiesHolding = sorter(
        companies.filter((company) => company.holding),
        "companyName"
      );

      return companiesHolding;
    } catch (error) {
      throw new Error(freire(texts.ERROR_LIST_COMPANIES));
    }
  }, [freire]);

  const fetchData = useCallback(async () => {
    try {
      setLoadingFetchData(true);

      // Resolve the current user's company on its own so the Tupi-only fields
      // render without waiting for the heavier company/theme lists. The
      // .catch is a no-op because the same rejection is surfaced by Promise.all.
      const userCompanyPromise = getCompanyCurrentUser();
      userCompanyPromise.then((userCompany) => setUserCompany(userCompany)).catch(() => {});

      const [companies, customerTypes, userCompany, themes] = await Promise.all(
        [
          getCompanies(),
          getCustomerTypes(),
          userCompanyPromise,
          getCustomThemes(),
        ]
      );

      setCompanies(companies);
      setCustomerType(customerTypes);
      setCustomThemes(themes);

      const isUserCompanyHolding = companies.find(
        (company) => company._id === userCompany?.id
      )?.holding;

      setUserCompany({ ...userCompany, holding: isUserCompanyHolding });
    } catch (error) {
      notification.error({
        message: freire(texts.OOPS),
        description: error.message,
      });
    } finally {
      setLoadingFetchData(false);
    }
  }, [
    freire,
    getCompanies,
    getCustomerTypes,
    getCustomThemes,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const submit = async () => {
    form.validateFieldsAndScroll(
      { scroll: { offsetTop: 75 } },
      async (err, values) => {
        if (err) return;
        delete values.hasParentCompanyPartner;
        values.iconPack = values.iconPack ?? null;
        values.cpms_custom_theme = values.cpms_custom_theme || null;
        const documentType = values.document_type;

        const trimmedDocument = values.document?.trim();

        if (trimmedDocument) {
          if (!validateDocumentByType(trimmedDocument, documentType)) {
            message.error(freire(texts.INVALID_DOCUMENT_FORMAT));
            return;
          }
          values.document = trimmedDocument;
        } else {
          values.document = null;
        }

        try {
          setLoadingSave(true);
          values.emails = emails;

          await api.post("company", values);

          setLoadingSave(false);

          notification.success({
            message: freire(texts.ALL_RIGHT),
            description: freire(texts.COMPANY_ADD_SUCCESS),
          });

          history.push("/dashboard/companys");
        } catch (error) {
          setLoadingSave(false);

          notification.error({
            message: freire(texts.WEN_WONG),
            description: error?.response?.data?.message || error.message,
          });
        }
      }
    );
  };

  // Customization -------------------------------------------------------------
  const canSetHasCompanyPin = hasPermission("set-has-company-pin");
  const canEditCompanyPin = hasPermission("edit-company-pin");
  const canEditCompanyPowerBiUrl = hasPermission("edit-company-powerbi-url");
  const canEditCompanyAllowedApps = hasPermission("edit-company-allowed-apps");
  const canEditCompanyCustomTheme = hasPermission(
    "edit-company-custom-theme"
  );
  // ---------------------------------------------------------------------------

  const isParentCompany = form.getFieldValue("isParentCompany");
  const hasParentCompanyPartner = form.getFieldValue("hasParentCompanyPartner");

  const parentCompanyID =
    form.getFieldValue("parentCompanyID") ?? userCompany?.id;

  const parentCompany = useMemo(
    () => companies?.find((company) => company._id === parentCompanyID),
    [companies, parentCompanyID]
  );

  const parentCompanyIconPack = useMemo(
    () =>
      availableIconPacks.find(
        (iconPack) => iconPack.alias === parentCompany?.iconPack
      ),
    [parentCompany, availableIconPacks]
  );

  useEffect(() => {
    if (!parentCompany) return;

    const hasIconPack = Boolean(parentCompany.iconPack);

    setHasIconPack(hasIconPack);
    form.setFieldsValue({
      iconPack: hasIconPack ? parentCompany?.iconPack : undefined,
      cpms_custom_theme: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentCompany]);

  const parentCompanyCountry = useMemo(
    () => parentCompany?.country ?? COUNTRY_BR,
    [parentCompany]
  );

  const showModal1 = () => {
    setIsModalVisible1(true);
  };

  const showModal2 = () => {
    setIsModalVisible2(true);
  };

  const showModal3 = () => {
    setIsModalVisible3(true);
  };

  const onChangeHasIconPack = (value) => {
    setHasIconPack(value);
    form.setFieldsValue({
      iconPack: value ? parentCompany?.iconPack : undefined,
    });
  };

  const onChangeIsParentCompany = (value) => {
    setHasIconPack(false);
    form.setFieldsValue({
      allowed_apps: value ? [BRAND_WHITELABEL_ALIAS] : [],
      iconPack: undefined,
      cpms_custom_theme: undefined,
    });
  };

  return (
    <div className="container">
      <div className="content">
        <Row>
          <Col span={4}>
            <Steps current={1} size="small" direction="vertical">
              <Step
                icon={<Icon type="profile" />}
                title={freire(texts.COMPANIES)}
                description={freire(texts.ADD_COMPANY)}
              />
            </Steps>
          </Col>

          <Col span={20}>
            <Form wrapperCol={{ span: 24 }}>
              <span className="titleNewCompanyEdt">{freire(texts.INFO)}</span>
              <Row gutter={[16, 0]}>
                <Col span={18}>
                  <Form.Item label={freire(texts.NAME)}>
                    {getFieldDecorator("companyName", {
                      rules: [
                        {
                          required: true,
                          message: freire(texts.REQUIRED),
                        },
                      ],
                    })(<Input placeholder={freire(texts.NAME)} />)}
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 0]}>
                <Col span={8}>
                  <Form.Item
                    label={freire(texts.SELECT_DOCUMENT_TYPE)}
                    required
                  >
                    {getFieldDecorator("document_type", {
                      initialValue: DOCUMENT_TYPES.CNPJ,
                      rules: [
                        {
                          required: true,
                          message: freire(texts.REQUIRED),
                        },
                      ],
                    })(
                      <Select
                        placeholder={freire(texts.SELECT_DOCUMENT_TYPE)}
                        onChange={handleDocumentTypeChange}
                      >
                        {Object.values(DOCUMENT_TYPES).map((value) => (
                          <Option key={value} value={value}>
                            {value}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item
                    label={freire(texts.COMPANY_DOCUMENT)}
                    required={!isDocumentOptional}
                  >
                    {getFieldDecorator("document", {
                      getValueFromEvent: (event) =>
                        formatDocumentByType(
                          event.target.value,
                          form.getFieldValue("document_type")
                        ),
                      rules: [
                        {
                          validator: validateCompanyDocument,
                        },
                      ],
                    })(<Input placeholder={"Ex.: 12.345.678/0009-10"} />)}
                  </Form.Item>
                </Col>
              </Row>
              <Show when={"create-parent-company"}>
                <Fragment>
                  <Row gutter={[16, 0]}>
                    <Col span={18}>
                      <Form.Item label={freire(texts.IS_PARENT_COMPANY)}>
                        {getFieldDecorator("isParentCompany", {
                          valuePropName: "checked",
                          initialValue: false,
                        })(
                          <Switch
                            checkedChildren={freire(texts.YES)}
                            unCheckedChildren={freire(texts.NO)}
                            onChange={onChangeIsParentCompany}
                          ></Switch>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                  {isParentCompany && (
                    <Row gutter={[16, 0]}>
                      <Col span={5}>
                        <Form.Item label={freire(texts.HAS_PARTNERSHIP)}>
                          {getFieldDecorator("hasParentCompanyPartner", {
                            valuePropName: "checked",
                            initialValue: false,
                          })(
                            <Switch
                              checkedChildren={freire(texts.YES)}
                              unCheckedChildren={freire(texts.NO)}
                              onChange={() =>
                                form.resetFields(["parent_company_partners"])
                              }
                            ></Switch>
                          )}
                        </Form.Item>
                      </Col>
                      <Col span={13}>
                        <Form.Item label={freire(texts.PARENT_COMPANY_PARTNER)}>
                          {getFieldDecorator("parent_company_partners", {
                            initialValue: [],
                            rules: [
                              {
                                required: hasParentCompanyPartner,
                                message: freire(texts.REQUIRED),
                              },
                            ],
                          })(
                            <Select
                              placeholder={freire(
                                texts.SELECT_PARENT_COMPANY_PARTNER
                              )}
                              mode="multiple"
                              disabled={
                                loadingFetchData ||
                                loadingSave ||
                                !hasParentCompanyPartner
                              }
                              loading={loadingFetchData}
                              showSearch
                              optionFilterProp="children"
                            >
                              {companies.map((company) => (
                                <Option key={company._id} value={company._id}>
                                  {company.companyName}
                                </Option>
                              ))}
                            </Select>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </Fragment>
              </Show>
              {!isParentCompany && companies.length > 0 && (
                <Show when="edit-parent-company">
                  <Row gutter={[16, 0]}>
                    <Col span={18}>
                      <Form.Item label={freire(texts.PARENT_COMPANY)}>
                        {getFieldDecorator("parentCompanyID", {
                          initialValue: userCompany?.id,
                        })(
                          <Select
                            placeholder={freire(texts.SELECT_PARENT_COMPANY)}
                            disabled={
                              loadingFetchData ||
                              loadingSave ||
                              !userCompany?.holding ||
                              companies.length === 1
                            }
                            loading={loadingFetchData}
                            showSearch
                            optionFilterProp="children"
                          >
                            {companies.map((company) => (
                              <Option key={company._id} value={company._id}>
                                {company.companyName}
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                </Show>
              )}

              <Row gutter={[16, 0]}>
                <Col span={8}>
                  <SelectCountry
                    form={form}
                    initialValue={parentCompanyCountry}
                    disabled={loadingFetchData || loadingSave}
                    dispatchFirstSelect
                  />
                </Col>
                <Col span={10}>
                  <Form.Item label={freire(texts.TYPE_OF_COSTUMER)}>
                    {getFieldDecorator(
                      "customerType",
                      {}
                    )(
                      <SelectWithNewItem
                        onAddNewItem={(itemName) => {
                          setCustomerType((prev) => [
                            ...prev,
                            { name: itemName },
                          ]);

                          form.setFieldsValue({ customerType: itemName });
                        }}
                        placeholder={freire(texts.PLACEHOLDER_TYPE_OF_COSTUMER)}
                      >
                        {customerTypes.map((customerType) => (
                          <Option
                            key={customerType.name}
                            value={customerType.name}
                          >
                            {customerType.name}
                          </Option>
                        ))}
                      </SelectWithNewItem>
                    )}
                  </Form.Item>
                </Col>
              </Row>

              {isTupiUser && (
                <Row gutter={[16, 0]}>
                  <Col span={10}>
                    <Form.Item label={freire(texts.FISCAL_MODEL)}>
                      {getFieldDecorator("fiscal_model")(
                        <Select
                          allowClear
                          placeholder={freire(
                            texts.PLACEHOLDER_FISCAL_MODEL
                          )}
                          onChange={(value) => setSelectedFiscalModel(value)}
                        >
                          {FISCAL_MODEL_OPTIONS.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      )}
                      {selectedFiscalModel === SOFTWARE_LICENSE && (
                        <Alert
                          type="warning"
                          showIcon
                          message={freire(texts.SOFTWARE_LICENSE_WARNING)}
                          style={{ width: "100%", marginBottom: 8 }}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label={freire(texts.BATCH_DURATION_TYPE)}
                      required
                    >
                      {getFieldDecorator("batchDurationType", {
                        rules: [
                          {
                            required: true,
                            message: freire(texts.REQUIRED),
                          },
                        ],
                      })(
                        <Select
                          placeholder={freire(
                            texts.PLACEHOLDER_BATCH_DURATION_TYPE
                          )}
                        >
                          {BATCH_DURATION_OPTIONS.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {freire(option.label)}
                            </Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {(canSetHasCompanyPin ||
                canEditCompanyPin ||
                canEditCompanyPowerBiUrl ||
                canEditCompanyAllowedApps) && (
                <span className="titleNewCompanyEdt">
                  {freire(texts.CUSTOMIZATION)}
                </span>
              )}

              <AlertParentCompanyIconPack
                visible={canSetHasCompanyPin && !isParentCompany}
                parentCompany={parentCompany}
                parentCompanyIconPack={parentCompanyIconPack}
              />

              <Row gutter={[16, 0]}>
                {canSetHasCompanyPin && (
                  <Col span={5}>
                    <Form.Item label={freire(texts.PINS_CUSTOMIZED)}>
                      <Switch
                        checkedChildren={freire(texts.YES)}
                        unCheckedChildren={freire(texts.NO)}
                        onChange={onChangeHasIconPack}
                        checked={hasIconPack}
                        disabled={
                          loadingFetchData ||
                          availableIconPacks.length === 0 ||
                          (!canEditCompanyPin && !parentCompanyIconPack)
                        }
                      />
                    </Form.Item>
                  </Col>
                )}

                <Col
                  span={13}
                  style={{ display: canEditCompanyPin ? "block" : "none" }}
                >
                  <Form.Item label={freire(texts.CHOOSE_PIN)}>
                    {getFieldDecorator("iconPack", {
                      rules: [
                        {
                          required: hasIconPack && canEditCompanyPin,
                          message: freire(texts.REQUIRED),
                        },
                      ],
                    })(
                      <Select
                        loading={loadingFetchData}
                        disabled={
                          loadingFetchData ||
                          !hasIconPack ||
                          availableIconPacks.length === 0
                        }
                        placeholder={freire(texts.SELECT)}
                        showSearch
                      >
                        {availableIconPacks.map((iconPack) => (
                          <Select.Option
                            key={iconPack.alias}
                            value={iconPack.alias}
                          >
                            {iconPack.name}
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
              </Row>

              {canEditCompanyCustomTheme && (
                <Row gutter={[16, 0]}>
                  <Col span={18}>
                    <Form.Item label={freire(texts.CPMS_CUSTOM_THEME)}>
                      {getFieldDecorator("cpms_custom_theme")(
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="children"
                          loading={loadingFetchData}
                          disabled={
                            loadingFetchData || customThemes.length === 0
                          }
                          placeholder={freire(texts.SELECT_CPMS_CUSTOM_THEME)}
                          filterOption={(input, option) =>
                            option.props.children
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {customThemes.map((theme) => (
                            <Option key={theme._id} value={theme._id}>
                              {theme.name}
                            </Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {canEditCompanyPowerBiUrl && (
                <Row gutter={[16, 0]}>
                  <Col span={18}>
                    <Form.Item label={freire(texts.URL_POWERBI)}>
                      {getFieldDecorator("powerbi", {
                        rules: [
                          {
                            required: false,
                            message: freire(texts.REQUIRED),
                          },
                        ],
                      })(<Input placeholder={freire(texts.URL_POWERBI)} />)}
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {canEditCompanyAllowedApps && (
                <Row gutter={[16, 0]}>
                  <Col span={18}>
                    <AllowedAppsForm
                      form={form}
                      loading={loadingFetchData}
                      isHolding={isParentCompany}
                      parentCompany={parentCompany}
                    />
                  </Col>
                </Row>
              )}

              <Row gutter={[16, 0]}>
                <Col span={24}>
                  <p className="titleNewCompanyEdt">{freire(texts.REPORT)}</p>
                  <Col span={24}>
                    <Form.Item label={freire(texts.EMAIL_LIST)}>
                      <p className="titleForSelectModal">
                        {freire(texts.EMAIL_REPORT_INFO)}
                        <span
                          style={{ cursor: "pointer", margin: "3px" }}
                          onClick={showModal1}
                        >
                          <strong>({freire(texts.EXAMPLE)})</strong>
                        </span>
                        <Modal
                          visible={isModalVisible1}
                          onCancel={() => setIsModalVisible1(false)}
                          footer={null}
                          maskClosable={true}
                        >
                          <img src={stationReportImg} alt="Station Report" />
                        </Modal>
                        {freire(texts.EMAIL_REPORT_INFO_TWO)}
                        <span
                          style={{ cursor: "pointer", margin: "3px" }}
                          onClick={showModal2}
                        >
                          <strong>({freire(texts.EXAMPLE)})</strong>
                        </span>
                        <Modal
                          visible={isModalVisible2}
                          onCancel={() => setIsModalVisible2(false)}
                          footer={null}
                          maskClosable={true}
                        >
                          <img src={MonthlyCouponReport} alt="Monthly Coupon" />
                        </Modal>
                        {freire(texts.EMAIL_REPORT_INFO_THREE)}
                        <span
                          style={{ cursor: "pointer", margin: "3px" }}
                          onClick={showModal3}
                        >
                          <strong>({freire(texts.EXAMPLE)})</strong>
                        </span>
                        <Modal
                          visible={isModalVisible3}
                          onCancel={() => setIsModalVisible3(false)}
                          footer={null}
                          maskClosable={true}
                        >
                          <img src={quotaReportImg} alt="Quota Report" />
                        </Modal>
                      </p>
                      {getFieldDecorator("emails")(
                        <Select
                          mode="tags"
                          disabled={loading}
                          style={{ width: "75%" }}
                          placeholder={freire(texts.INPUT_EMAILS)}
                          onSelect={(value) => {
                            if (!isValidEmail(value)) {
                              message.error(freire(texts.INFORM_VALID_EMAIL));
                              const newEmails = [...emails];
                              const index = newEmails.indexOf(value);
                              if (index > -1) {
                                newEmails.splice(index, 1);
                                setEmails(newEmails);
                              }
                            } else {
                              setEmails([...emails, value]);
                            }
                          }}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Col>
              </Row>

              <div className="footer">
                <Button loading={loadingSave} onClick={submit} type="primary">
                  {freire(texts.SAVE)}
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const PageNewCompany = Form.create({ name: "inputs" })(NewCompany);
export default withPermission(withRouter(PageNewCompany));
